import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFilter, FaCheckCircle, FaTag, FaPlus } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { GetRoleByCondition, GetRoles } from '../../_core/_request.ts';
import { RoleResponseModel } from '../../_core/_model';
import { Separators } from '../../Constants.ts';
import RoleCondition from '../Roles/RoleCondition.tsx';
import { useAppContext } from '../../context/AppContext.tsx';
import LoadingOverlay from '../../UIComponents/LoadingOverlay.tsx';
import { Modal, Spinner } from 'react-bootstrap';
import RoleEditor from '../Filters/RoleEditor.tsx';
import TooltipButton from '../Tools/TooltipButton.tsx';
import { FiArrowLeft } from 'react-icons/fi';

interface request {
    roleToReplace: RoleResponseModel,
    onSelect: (r: RoleResponseModel) => void,
    onClose: () => void
}

const RoleForConditions = ({ roleToReplace, onSelect, onClose }: request) => {
    const [roles, setRoles] = useState<RoleResponseModel[] | null>(null);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<RoleResponseModel | null>(null);
    const [expandedRole, setExpandedRole] = useState<number | null>(null);
    const { dimensions } = useAppContext();
    const [showNewRole, setShowNewRole] = useState<boolean>(false);

    const getRoleId = () => {
        GetRoleByCondition(roleToReplace.filterConditions, roleToReplace.dataMapCode).then((r) => {
            if (r.data > 0)
                GetRoles({ roleID: r.data.toString(), roleName: "", roleCode: "", dataMapColumn: "", roleGroupID: null, userID: null }).then(res => setRoles(res.data));
            else
                setRoles([]);
        });
    };

    useEffect(() => {
        getRoleId();
    }, [roleToReplace]);

    const filtered = roles?.filter(role =>
        role.roleName.toLowerCase().includes(search.toLowerCase()) ||
        role.description.toLowerCase().includes(search.toLowerCase())
    );

    const verifyMatch = (candidate: RoleResponseModel) =>
        candidate.dataMapCode === roleToReplace.dataMapCode;

    const roleRef = useRef<any>(null);
    const [progress, setProgress] = useState<string | null>(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const [canSave, setCanSave] = useState<boolean>(false);

    return (
        <div className="h-100 p-4 overflow-y-auto">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h2 className="mb-1">Replace Role</h2>
                    <p className="text-danger fw-semibold small mb-0">Please select a role to continue</p>
                </div>
                <TooltipButton title="Back to Request Editor">
                    <button className="btn shadow-sm border" onClick={onClose}>
                        <FiArrowLeft size={20} />
                    </button>
                </TooltipButton>
            </div>

            <div className="mb-3">
                <div className="alert alert-warning d-flex align-items-center gap-2 py-2 small mb-0">
                    <FaTag />
                    <div>Only roles matching the current filter condition will appear.</div>
                </div>
            </div>

            <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <AnimatePresence>
                    {
                        roles === null ? (
                            <LoadingOverlay msg="Fetching role for condition" />
                        ) : roles.length === 0 ? (
                            <motion.div
                                className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 w-100"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                style={{
                                    border: "2px dashed #dee2e6",
                                    borderRadius: "10px",
                                    marginTop: "40px",
                                    backgroundColor: "#fdf8f1",
                                }}
                            >
                                <FaFilter size={60} color="#ffc107" className="mb-3" />
                                <h5 className="fw-semibold text-warning">No Role Found</h5>
                                <div className="text-center d-inline-flex flex-row align-items-center gap-2">
                                    No role exists for this condition. Click
                                    <button
                                        className='btn btn-outline-primary d-flex flex-row align-items-center gap-2'
                                        onClick={() => setShowNewRole(true)}
                                    >
                                        <FaPlus />Create new Role
                                    </button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                {filtered?.map(role => {
                                    const isSelected = selected?.roleID === role.roleID;
                                    const color = dimensions.find((r) => r.dimensionCode === role.dataMapCode)?.color || '#333';
                                    const bg = dimensions.find((r) => r.dimensionCode === role.dataMapCode)?.bg || '#f9f9f9';
                                    return (
                                        <motion.div
                                            key={role.roleID}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 10 }}
                                            transition={{ duration: 0.2 }}
                                            className={`role-card rounded px-3 py-2 mb-3 ${isSelected ? 'border border-3 border-primary bg-light' : ''}`}
                                            style={{
                                                background: bg,
                                                color: color,
                                                borderLeft: `5px solid ${color}`
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="d-flex flex-row w-100 align-items-center justify-content-between">
                                                    <div className="d-flex flex-row justify-content-between align-items-center w-100">
                                                        <div className="d-flex flex-row gap-1 align-items-center text-truncate">
                                                            <FaTag />
                                                            <strong>{role.roleName}</strong>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <small>{role.description}</small>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 w-100 justify-content-end mt-2">
                                                        {isSelected ? (
                                                            <div className="badge bg-primary text-white d-flex align-items-center gap-1">
                                                                <FaCheckCircle size={14} />
                                                                Selected
                                                            </div>
                                                        ) : verifyMatch(role) && (
                                                            <div className="badge bg-success-subtle text-success d-flex align-items-center gap-1">
                                                                <FaCheckCircle size={14} />
                                                                Matching Role – Please select
                                                            </div>
                                                        )}
                                                        {role.filterConditions && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setExpandedRole(expandedRole === role.roleID ? null : role.roleID);
                                                                }}
                                                                className="btn btn-link p-0"
                                                            >
                                                                <div className='d-flex flex-row align-items-center gap-2'>
                                                                    {expandedRole !== role.roleID ? "View Conditions" : "Hide Conditions"}
                                                                    {expandedRole === role.roleID ? <FaChevronUp /> : <FaChevronDown />}
                                                                </div>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <AnimatePresence>
                                                {expandedRole === role.roleID && role.filterConditions && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="mt-3 pt-2 border-top"
                                                    >
                                                        <h6 className="mb-2">Filter Conditions:</h6>
                                                        {role.filterConditions.split(Separators.row).map((condition, idx) => (
                                                            <div key={idx} className="border p-2 rounded-3 mb-2">
                                                                <RoleCondition condition={condition} />
                                                            </div>
                                                        ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>

                                            <div className='d-flex flex-row align-items-center justify-content-end w-100 mt-3'>
                                                <button
                                                    className={`btn btn-${isSelected ? 'success' : 'outline-primary'} btn-sm fw-bold`}
                                                    onClick={() => {
                                                        setSelected(role);
                                                        if (onSelect) onSelect(role);
                                                    }}
                                                >
                                                    {isSelected ? 'Selected' : 'Click to Select'}
                                                </button>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        )
                    }
                </AnimatePresence>
            </div>

            <Modal show={showNewRole} onHide={onClose} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Create new Role</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RoleEditor
                        canSaveCall={setCanSave}
                        ref={roleRef}
                        roleId={null}
                        onClose={() => {
                            getRoleId();
                            setShowNewRole(false);
                        }}
                        preFills={{
                            dataMapCode: roleToReplace.dataMapCode,
                            sConditions: roleToReplace.filterConditions
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    {
                        (progress == null) ?
                            (canSave ?
                                <button className="btn btn-success py-2 px-4 d-flex flex-row align-items-center gap-1 text-nowrap" onClick={() => { roleRef.current.SaveRole(setProgress) }}>
                                    <FaCheckCircle />Save & Close</button>
                                :
                                <button className="btn bg-secondary text-white border py-2 px-4 d-flex flex-row align-items-center gap-1 text-nowrap" style={{ opacity: "0.5", cursor: "not-allowed" }}> <FaCheckCircle />Save & Close</button>) :
                            <button className="btn btn-warning py-2 px-4">
                                <div className='d-flex flex-row align-items-center gap-2 text-nowrap'>
                                    <Spinner animation="border" size='sm' /> <span>Save in progress</span>
                                </div>
                            </button>
                    }
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RoleForConditions;
